import {
  PIECE_VALUES,
  PAWN_TABLE,
  KNIGHT_TABLE,
  BISHOP_TABLE,
  ROOK_TABLE,
  QUEEN_TABLE,
  KING_MIDDLE_TABLE,
  KING_END_TABLE
} from './constants';

const PIECE_TABLES = {
  p: PAWN_TABLE,
  n: KNIGHT_TABLE,
  b: BISHOP_TABLE,
  r: ROOK_TABLE,
  q: QUEEN_TABLE,
  k: KING_MIDDLE_TABLE
};

// Convert square to index (0-63)
function squareToIndex(square) {
  const file = square.charCodeAt(0) - 97; // 'a' = 0
  const rank = 8 - parseInt(square[1]);   // '8' = 0, '1' = 7
  return rank * 8 + file;
}

// Check if it's endgame (few pieces left)
function isEndgame(board) {
  let pieceCount = 0;
  const squares = board.board().flat();
  
  for (const piece of squares) {
    if (piece && piece.type !== 'p' && piece.type !== 'k') {
      pieceCount++;
    }
  }
  
  return pieceCount <= 6;
}

// Get piece-square table value
function getPieceSquareValue(piece, square, isEndgame = false) {
  const index = squareToIndex(square);
  let table = PIECE_TABLES[piece.type];
  
  // Use endgame table for king
  if (piece.type === 'k' && isEndgame) {
    table = KING_END_TABLE;
  }
  
  // Flip index for black pieces
  const tableIndex = piece.color === 'w' ? index : 63 - index;
  
  return table[tableIndex];
}

// Count material for a color
function countMaterial(board, color) {
  let material = 0;
  const squares = board.board().flat();
  
  for (const piece of squares) {
    if (piece && piece.color === color) {
      material += PIECE_VALUES[piece.type];
    }
  }
  
  return material;
}

// Evaluate pawn structure
function evaluatePawnStructure(board, color) {
  let score = 0;
  const boardArray = board.board();
  const pawns = [];
  
  // Find all pawns
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = boardArray[rank][file];
      if (piece && piece.type === 'p' && piece.color === color) {
        pawns.push({ rank, file });
      }
    }
  }
  
  // Check for doubled pawns
  const filesWithPawns = {};
  for (const pawn of pawns) {
    if (!filesWithPawns[pawn.file]) {
      filesWithPawns[pawn.file] = 0;
    }
    filesWithPawns[pawn.file]++;
  }
  
  for (const file in filesWithPawns) {
    if (filesWithPawns[file] > 1) {
      score -= 20 * (filesWithPawns[file] - 1); // Doubled pawns penalty
    }
  }
  
  // Check for isolated pawns
  for (const pawn of pawns) {
    const hasNeighbor = pawns.some(p => 
      Math.abs(p.file - pawn.file) === 1 && p !== pawn
    );
    if (!hasNeighbor) {
      score -= 15; // Isolated pawn penalty
    }
  }
  
  // Check for passed pawns
  for (const pawn of pawns) {
    let isPassed = true;
    const direction = color === 'w' ? -1 : 1;
    
    // Check if any enemy pawns can block or capture
    for (let rank = pawn.rank + direction; rank >= 0 && rank < 8; rank += direction) {
      for (let fileOffset = -1; fileOffset <= 1; fileOffset++) {
        const file = pawn.file + fileOffset;
        if (file >= 0 && file < 8) {
          const piece = boardArray[rank][file];
          if (piece && piece.type === 'p' && piece.color !== color) {
            isPassed = false;
            break;
          }
        }
      }
      if (!isPassed) break;
    }
    
    if (isPassed) {
      // Bonus based on how advanced the pawn is
      const advancement = color === 'w' ? (7 - pawn.rank) : pawn.rank;
      score += 20 + (advancement * 10);
    }
  }
  
  return score;
}

// Evaluate king safety
function evaluateKingSafety(board, color) {
  let score = 0;
  const boardArray = board.board();
  
  // Find king position
  let kingPos = null;
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = boardArray[rank][file];
      if (piece && piece.type === 'k' && piece.color === color) {
        kingPos = { rank, file };
        break;
      }
    }
    if (kingPos) break;
  }
  
  if (!kingPos) return 0;
  
  // Bonus for castled king (king on g or c file, near corner)
  if ((kingPos.file === 6 || kingPos.file === 1) && 
      ((color === 'w' && kingPos.rank === 7) || (color === 'b' && kingPos.rank === 0))) {
    score += 50;
    
    // Check pawn shield
    const pawnShieldRank = color === 'w' ? 6 : 1;
    for (let f = kingPos.file - 1; f <= kingPos.file + 1; f++) {
      if (f >= 0 && f < 8) {
        const piece = boardArray[pawnShieldRank][f];
        if (piece && piece.type === 'p' && piece.color === color) {
          score += 10;
        }
      }
    }
  }
  
  return score;
}

// Check for bishop pair
function hasBishopPair(board, color) {
  let bishopCount = 0;
  const squares = board.board().flat();
  
  for (const piece of squares) {
    if (piece && piece.type === 'b' && piece.color === color) {
      bishopCount++;
    }
  }
  
  return bishopCount >= 2;
}

// Main evaluation function
export function evaluate(board) {
  if (board.isCheckmate()) {
    return board.turn() === 'w' ? -Infinity : Infinity;
  }
  
  if (board.isDraw() || board.isStalemate()) {
    return 0;
  }
  
  const endgame = isEndgame(board);
  let score = 0;
  
  // Material difference
  score += countMaterial(board, 'w') - countMaterial(board, 'b');
  
  // Piece-square tables
  const squares = board.board();
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = squares[rank][file];
      if (piece) {
        const square = String.fromCharCode(97 + file) + (8 - rank);
        const psValue = getPieceSquareValue(piece, square, endgame);
        score += piece.color === 'w' ? psValue : -psValue;
      }
    }
  }
  
  // Pawn structure
  score += evaluatePawnStructure(board, 'w');
  score -= evaluatePawnStructure(board, 'b');
  
  // King safety (less important in endgame)
  if (!endgame) {
    score += evaluateKingSafety(board, 'w');
    score -= evaluateKingSafety(board, 'b');
  }
  
  // Bishop pair bonus
  if (hasBishopPair(board, 'w')) score += 30;
  if (hasBishopPair(board, 'b')) score -= 30;
  
  // Center control
  score += evaluateCenterControl(board, 'w');
  score -= evaluateCenterControl(board, 'b');
  
  // Mobility (simplified - count legal moves)
  score += evaluateMobility(board) * 5;
  
  // Development in opening
  if (board.history().length < 20) {
    score += evaluateDevelopment(board, 'w');
    score -= evaluateDevelopment(board, 'b');
  }
  
  return score;
}

// Evaluate center control
function evaluateCenterControl(board, color) {
  let score = 0;
  const centerSquares = ['d4', 'd5', 'e4', 'e5'];
  const extendedCenter = ['c3', 'c4', 'c5', 'c6', 'd3', 'd6', 'e3', 'e6', 'f3', 'f4', 'f5', 'f6'];
  
  for (const sq of centerSquares) {
    const piece = board.get(sq);
    if (piece && piece.color === color) {
      score += 15; // Occupying center
      if (piece.type === 'p') score += 10; // Pawn in center extra bonus
    }
  }
  
  for (const sq of extendedCenter) {
    const piece = board.get(sq);
    if (piece && piece.color === color) {
      score += 5; // Controlling extended center
    }
  }
  
  return score;
}

// Evaluate piece development
function evaluateDevelopment(board, color) {
  let score = 0;
  const backRank = color === 'w' ? '1' : '8';
  const knightSquares = ['b' + backRank, 'g' + backRank];
  const bishopSquares = ['c' + backRank, 'f' + backRank];
  
  // Penalty for undeveloped knights
  for (const sq of knightSquares) {
    const piece = board.get(sq);
    if (piece && piece.type === 'n' && piece.color === color) {
      score -= 20;
    }
  }
  
  // Penalty for undeveloped bishops
  for (const sq of bishopSquares) {
    const piece = board.get(sq);
    if (piece && piece.type === 'b' && piece.color === color) {
      score -= 15;
    }
  }
  
  // Bonus for castling rights
  const castlingRights = board.getCastlingRights(color);
  if (castlingRights.k || castlingRights.q) {
    score += 10;
  }
  
  return score;
}

// Evaluate mobility (difference in legal moves)
export function evaluateMobility(board) {
  const currentTurn = board.turn();
  const currentMoves = board.moves().length;
  
  // We can't easily get opponent's moves without changing turn
  // So we estimate based on current position
  return currentTurn === 'w' ? currentMoves * 2 : -currentMoves * 2;
}

// Classify move quality based on evaluation change
export function classifyMove(evalBefore, evalAfter, wasPlayerMove, isBestMove) {
  if (isBestMove) return 'excellent';
  
  // Evaluation loss from player's perspective
  const evalLoss = wasPlayerMove ? (evalBefore - evalAfter) : (evalAfter - evalBefore);
  
  if (evalLoss <= 10) return 'good';
  if (evalLoss <= 50) return 'inaccuracy';
  if (evalLoss <= 150) return 'mistake';
  return 'blunder';
}
